<script>
import { PedalKnob } from '@midulate/shared';

export default {
  components: { PedalKnob },

  props: {
    patch: {
      type: Object,
      required: true
    },

    tempoInfo: {
      type: Object,
      required: false,
      default: null
    },

    capabilities: {
      type: Object,
      required: false,
      default: () => ({ capabilities: {} })
    }
  },

  emits: ['update:patch'],

  computed: {
    clockSource() {
      return this.patch.clock_source ?? 'internal';
    },

    meter() {
      return this.patch.meter ?? 4;
    },

    swing() {
      return this.patch.swing ?? 0;
    },

    usingMidiClock() {
      return this.clockSource === 'midi_clock';
    }
  },

  methods: {
    update(field, value) {
      this.$emit('update:patch', { ...this.patch, [field]: value });
    },

    onClockToggle(event) {
      this.update('clock_source', event.target.checked ? 'midi_clock' : 'internal');
    }
  }
}
</script>

<template>
  <div class="patch-globals mt-4">
    <!-- Name -->
    <label class="input">
      <span class="label w-60">Name</span>
      <input
        type="text"
        :value="patch.title"
        @input="update('title', $event.target.value)"
      />
    </label>

    <fieldset class="patch-globals__clock-section bg-base-100 border-base-300 rounded-box border p-4 mt-4">
      <legend>Clock settings</legend>

      <div class="flex flex-col gap-y-3 pt-2 sm:flex-row sm:flex-wrap sm:items-stretch sm:gap-x-10 sm:gap-y-3">

        <!-- MIDI Clock Sync -->
        <div class="flex items-center gap-3 sm:flex-col sm:justify-between sm:items-start">
          <span class="text-sm text-base-content/70 w-32 shrink-0 sm:w-auto">MIDI Clock Sync</span>
          <div class="flex flex-1 items-center gap-2">
            <input
              type="checkbox"
              class="toggle toggle-sm"
              :checked="usingMidiClock"
              @change="onClockToggle"
            />
            <span
              class="badge badge-sm badge-neutral min-w-[4.5rem] justify-center"
              :class="{ 'opacity-30': !usingMidiClock }"
            >
              {{ usingMidiClock && tempoInfo?.synced ? `${Math.round(tempoInfo.bpm)} BPM` : 'No clock' }}
            </span>
          </div>
        </div>

        <!-- Tempo (BPM) -->
        <div
          class="flex items-center gap-3 sm:flex-col sm:justify-between sm:items-start"
          :class="{ 'opacity-40': usingMidiClock }"
        >
          <span class="text-sm text-base-content/70 w-32 shrink-0 sm:w-auto">Tempo (BPM)</span>
          <input
            class="input input-sm w-24"
            type="number"
            :value="patch.bpm"
            :disabled="usingMidiClock"
            @input="update('bpm', Number($event.target.value))"
          />
        </div>

        <!-- Meter -->
        <div class="flex items-center gap-3 sm:flex-col sm:justify-between sm:items-start">
          <span class="text-sm text-base-content/70 w-32 shrink-0 sm:w-auto">Meter</span>
          <input
            class="input input-sm w-16 text-center"
            type="number"
            min="1"
            max="16"
            :value="meter"
            @input="update('meter', Math.min(16, Math.max(1, Number($event.target.value))))"
          />
        </div>

        <!-- Swing — only shown when the firmware reports swing support -->
        <PedalKnob
          v-if="capabilities.capabilities?.swing"
          label="Swing"
          tip="Phase offset of all clock-synced effects relative to beat one. Negative pushes effects before the beat, positive pushes after."
          mode="bipolar"
          :value="swing"
          @update:value="update('swing', $event)"
        />

      </div>
    </fieldset>
  </div>
</template>
